import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconButton, Tooltip } from "@datapay-by-datacom/design-system";
import { FiChevronLeft, FiChevronRight, FiSearch } from "react-icons/fi";
import type { CompanyShellRow } from "../../generated/core-api";
import { companyCellEditKey } from "../components/companyCellEditKey";
import { inlineCompanyFieldIssues } from "../components/inlineCompanyFieldIssues";
import {
  cellErrorKey,
  CSV_HEADER_TO_FIELD_PATH,
  formatCompanyShellRowForPreview,
  unknownShellCsvColumnCellKey,
} from "../hooks";
import type {
  CompanyShellCsvColumn,
  CompanyShellNormalisation,
} from "../types";
import {
  changedCompanyShellDraftHeaders,
  COMPANY_SHELL_FIELD_GROUPS,
  COMPANY_SHELL_IDENTITY_HEADERS,
  createCompanyShellDraft,
  type CompanyShellDraft,
} from "./companyShellEditorDraft";
import { CompanyShellEditModal } from "./CompanyShellEditModal";
import { DiscardCompanyChangesConfirmModal } from "./DiscardCompanyChangesConfirmModal";
import s from "./CompanyShellPreview.module.css";

const IDENTITY_HEADERS = COMPANY_SHELL_IDENTITY_HEADERS;
const FIELD_GROUPS = COMPANY_SHELL_FIELD_GROUPS;

function headerToIdFragment(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function isBlank(value: string): boolean {
  return value.trim() === "";
}

const SECTION_ACTIVATION_GAP = 8;

function verticalScrollContainer(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return parent;
    parent = parent.parentElement;
  }
  return null;
}

function isAtScrollEnd(container: HTMLElement | null): boolean {
  if (container) {
    return (
      container.scrollHeight > container.clientHeight &&
      container.scrollTop + container.clientHeight >=
        container.scrollHeight - 2
    );
  }

  const root = document.documentElement;
  return (
    root.scrollHeight > window.innerHeight &&
    window.scrollY + window.innerHeight >= root.scrollHeight - 2
  );
}

export interface CompanyShellPreviewProps {
  columns: CompanyShellCsvColumn[];
  rows: CompanyShellRow[];
  /** When true, an explicit whole-company editing action is available. */
  editable?: boolean;
  /** Prevents opening the editor while validation or creation is running. */
  isDisabled?: boolean;
  onCompanySave?: (
    rowArrayIndex: number,
    draft: CompanyShellDraft
  ) => void;
  onEditingChange?: (isEditing: boolean) => void;
  /** Hides the editor discard dialog while the batch leave dialog is active. */
  suppressDiscardConfirmation?: boolean;
  cellErrors?: Map<string, string[]>;
  errorsByRow?: Map<number, string[]>;
  /** Cells saved during this review session. */
  editedCellKeys?: ReadonlySet<string>;
  normalisations?: CompanyShellNormalisation[];
  /** Source CSV line for each row, using the same array index as `rows`. */
  rowCsvLineNumbers?: number[];
  ariaLabel?: string;
  /** Increment to select and focus the first company/field with a validation issue. */
  focusFirstIssueRequest?: number;
}

/**
 * One parsed company at a time, chosen from a searchable switcher. Every CSV
 * field keeps a stable place in the layout; blank values render as an em dash
 * in preview mode. Requiredness appears only beside blank fields.
 */
export function CompanyShellPreview({
  columns,
  rows,
  editable = false,
  isDisabled = false,
  onCompanySave,
  onEditingChange,
  suppressDiscardConfirmation = false,
  cellErrors,
  errorsByRow,
  editedCellKeys,
  normalisations,
  rowCsvLineNumbers,
  ariaLabel = "Parsed companies",
  focusFirstIssueRequest = 0,
}: CompanyShellPreviewProps) {
  const [selectedIndex, setIndex] = useState(0);
  // A replacement CSV can contain fewer companies than the previous file.
  // Clamp synchronously for this render; the effect below then persists the
  // corrected index. Waiting for the effect alone leaves one render where
  // `companyLabels[index]` is undefined and crashes the preview.
  const index =
    rows.length === 0 ? 0 : Math.min(selectedIndex, rows.length - 1);
  const [query, setQuery] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [activeSection, setActiveSection] = useState("identity");
  const [editor, setEditor] = useState<{
    rowIndex: number;
    original: CompanyShellDraft;
    draft: CompanyShellDraft;
  } | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saveAnnouncement, setSaveAnnouncement] = useState("");
  const handledFocusRequestRef = useRef(0);
  const programmaticSectionRef = useRef<string | null>(null);
  const shellRef = useRef<HTMLElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const shouldReturnFocusRef = useRef(false);

  useEffect(() => {
    setIndex((current) => {
      if (rows.length === 0) return 0;
      return Math.min(current, rows.length - 1);
    });
  }, [rows.length]);

  useEffect(() => {
    if (suppressDiscardConfirmation) setDiscardConfirmOpen(false);
  }, [suppressDiscardConfirmation]);

  useEffect(() => {
    if (editor || !shouldReturnFocusRef.current) return;
    shouldReturnFocusRef.current = false;
    editButtonRef.current?.focus({ preventScroll: true });
  }, [editor]);

  const companyLabels = useMemo(
    () =>
      rows.map((company, companyIndex) => {
        const companyDisplay = formatCompanyShellRowForPreview(company);
        const issueMessages = errorsByRow?.get(companyIndex) ?? [];
        const sourceRow = rowCsvLineNumbers?.[companyIndex] ?? companyIndex + 1;
        const code = companyDisplay["Company Code"] || "Missing company code";
        const name = companyDisplay["Company Name"] || "Missing company name";
        return {
          index: companyIndex,
          code,
          name,
          sourceRow,
          issueCount: issueMessages.length,
          searchText:
            `${code} ${name} row ${sourceRow} ${issueMessages.join(" ")}`.toLowerCase(),
        };
      }),
    [errorsByRow, rowCsvLineNumbers, rows]
  );

  const needle = query.trim().toLowerCase();
  const listedCompanies = needle
    ? companyLabels.filter((company) => company.searchText.includes(needle))
    : companyLabels;

  const selectedCompany = companyLabels[index];
  const activeSuggestion = listedCompanies[activeSuggestionIndex];
  const editorIsDirty = useMemo(
    () =>
      editor !== null &&
      changedCompanyShellDraftHeaders(
        editor.original,
        editor.draft,
        columns
      ).length > 0,
    [columns, editor]
  );

  const chooseCompany = (companyIndex: number) => {
    if (editor) return;
    setIndex(companyIndex);
    setActiveSection("identity");
    setQuery("");
    setSuggestionsOpen(false);
  };

  const columnsByHeader = useMemo(() => {
    const map = new Map<string, CompanyShellCsvColumn>();
    for (const column of columns) map.set(column.header, column);
    return map;
  }, [columns]);

  const normalisationsByCell = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of normalisations ?? []) {
      map.set(cellErrorKey(item.row_index, item.column), item.normalised_value);
    }
    return map;
  }, [normalisations]);

  const groupedHeaders = useMemo(() => {
    const known = new Set(FIELD_GROUPS.flatMap((group) => group.headers));
    const other = columns
      .map((column) => column.header)
      .filter(
        (header) =>
          !IDENTITY_HEADERS.includes(header as (typeof IDENTITY_HEADERS)[number]) &&
          !known.has(header)
      );
    return [
      ...FIELD_GROUPS.map((group) => ({
        id: group.id,
        title: group.title,
        headers: group.headers.filter((header) => columnsByHeader.has(header)),
      })),
      { id: "other", title: "Other", headers: other },
    ];
  }, [columns, columnsByHeader]);

  const sectionIds = useMemo(
    () => [
      "identity",
      ...groupedHeaders
        .filter((group) => group.headers.length > 0)
        .map((group) => group.id),
    ],
    [groupedHeaders]
  );

  const scrollToGroup = useCallback(
    (groupId: string, behavior: ScrollBehavior = "smooth") => {
      const target = document.getElementById(
        `company-${index}-${groupId}`
      );
      const shell = shellRef.current;
      if (!target || !shell) return;

      programmaticSectionRef.current = groupId;
      setActiveSection(groupId);
      const container = verticalScrollContainer(shell);
      const targetTop = target.getBoundingClientRect().top;
      const toolbarHeight =
        toolbarRef.current?.getBoundingClientRect().height ?? 0;

      if (container) {
        const containerTop = container.getBoundingClientRect().top;
        container.scrollTo({
          top: Math.max(
            0,
            container.scrollTop +
              targetTop -
              containerTop -
              toolbarHeight -
              SECTION_ACTIVATION_GAP
          ),
          behavior,
        });
        return;
      }

      window.scrollTo({
        top: Math.max(
          0,
          window.scrollY -
            toolbarHeight -
            SECTION_ACTIVATION_GAP +
            targetTop
        ),
        behavior,
      });
    },
    [index]
  );

  const fieldReasonsFor = useCallback(
    (rowIndex: number, header: string): string[] => {
      const fieldPath = Object.prototype.hasOwnProperty.call(
        CSV_HEADER_TO_FIELD_PATH,
        header
      )
        ? CSV_HEADER_TO_FIELD_PATH[header]
        : unknownShellCsvColumnCellKey(header);
      return cellErrors?.get(cellErrorKey(rowIndex, fieldPath)) ?? [];
    },
    [cellErrors]
  );

  const closeEditor = () => {
    setEditor(null);
    setDiscardConfirmOpen(false);
    shouldReturnFocusRef.current = true;
    onEditingChange?.(false);
  };

  const openEditor = () => {
    const original = createCompanyShellDraft(rows[index], columns);
    setEditor({
      rowIndex: index,
      original,
      draft: { ...original },
    });
    setSaveAnnouncement("");
    setSuggestionsOpen(false);
    setQuery("");
    onEditingChange?.(true);
  };

  const requestCancelEditor = () => {
    if (editorIsDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    closeEditor();
  };

  const saveEditor = () => {
    if (!editor || !editorIsDirty) return;
    onCompanySave?.(editor.rowIndex, editor.draft);
    const savedCompany =
      editor.draft["Company Name"] ||
      editor.draft["Company Code"] ||
      `row ${rowCsvLineNumbers?.[editor.rowIndex] ?? editor.rowIndex + 1}`;
    setSaveAnnouncement(`Changes saved for ${savedCompany}.`);
    closeEditor();
  };

  useEffect(() => {
    if (
      focusFirstIssueRequest === 0 ||
      focusFirstIssueRequest === handledFocusRequestRef.current ||
      !errorsByRow ||
      editor !== null
    ) {
      return;
    }
    handledFocusRequestRef.current = focusFirstIssueRequest;

    const firstIssueRow = rows.findIndex(
      (_, rowIndex) => (errorsByRow.get(rowIndex)?.length ?? 0) > 0
    );
    if (firstIssueRow < 0) return;

    const original = createCompanyShellDraft(
      rows[firstIssueRow],
      columns
    );
    setIndex(firstIssueRow);
    setEditor({
      rowIndex: firstIssueRow,
      original,
      draft: { ...original },
    });
    setSaveAnnouncement("");
    setSuggestionsOpen(false);
    setQuery("");
    onEditingChange?.(true);
  }, [
    columns,
    errorsByRow,
    focusFirstIssueRequest,
    onEditingChange,
    rows,
    editor,
  ]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const container = verticalScrollContainer(shell);
    const targets = sectionIds
      .map((groupId) => document.getElementById(`company-${index}-${groupId}`))
      .filter((target): target is HTMLElement => target !== null);

    const updateActiveSection = () => {
      const sections = targets.map((target) => ({
        id: target.id.replace(`company-${index}-`, ""),
        rect: target.getBoundingClientRect(),
      }));
      const hasLayout = sections.some(
        ({ rect }) =>
          rect.height > 0 || rect.top !== 0 || rect.bottom !== 0
      );
      if (!hasLayout || sections.length === 0) return;

      const activationLine =
        (toolbarRef.current?.getBoundingClientRect().bottom ?? 0) +
        SECTION_ACTIVATION_GAP;
      const isAtEnd = isAtScrollEnd(container);
      const programmaticSection = programmaticSectionRef.current;
      if (programmaticSection) {
        const targetSection = sections.find(
          (section) => section.id === programmaticSection
        );
        const isAligned =
          targetSection !== undefined &&
          Math.abs(targetSection.rect.top - activationLine) <= 4;
        if (!isAligned && !isAtEnd) {
          setActiveSection(programmaticSection);
          return;
        }
        programmaticSectionRef.current = null;
      }

      let nextSection = sections[0].id;
      for (const section of sections) {
        if (section.rect.top > activationLine) break;
        nextSection = section.id;
      }
      if (isAtEnd) {
        nextSection = sections[sections.length - 1].id;
      }
      setActiveSection((current) =>
        current === nextSection ? current : nextSection
      );
    };

    const scrollTarget: HTMLElement | Window = container ?? window;
    const cancelProgrammaticScroll = () => {
      programmaticSectionRef.current = null;
    };
    scrollTarget.addEventListener("scroll", updateActiveSection, {
      passive: true,
    });
    scrollTarget.addEventListener("wheel", cancelProgrammaticScroll, {
      passive: true,
    });
    scrollTarget.addEventListener("touchstart", cancelProgrammaticScroll, {
      passive: true,
    });
    window.addEventListener("resize", updateActiveSection);
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateActiveSection);
    resizeObserver?.observe(shell);
    if (toolbarRef.current) resizeObserver?.observe(toolbarRef.current);
    updateActiveSection();

    return () => {
      scrollTarget.removeEventListener("scroll", updateActiveSection);
      scrollTarget.removeEventListener("wheel", cancelProgrammaticScroll);
      scrollTarget.removeEventListener(
        "touchstart",
        cancelProgrammaticScroll
      );
      window.removeEventListener("resize", updateActiveSection);
      resizeObserver?.disconnect();
    };
  }, [index, sectionIds]);

  const visibleGroups = useMemo(
    () => groupedHeaders.filter((group) => group.headers.length > 0),
    [groupedHeaders]
  );

  if (rows.length === 0) {
    return null;
  }

  const row = rows[index];
  const display = formatCompanyShellRowForPreview(row);
  const allReasons = [...new Set(errorsByRow?.get(index) ?? [])];

  const fieldReasons = (header: string): string[] =>
    fieldReasonsFor(index, header);

  const valueFor = (header: string): string => display[header] ?? "";
  const isEdited = (header: string): boolean =>
    editedCellKeys?.has(companyCellEditKey(index, header)) ?? false;

  const code = valueFor("Company Code");
  const name = valueFor("Company Name");
  const codeReasons = fieldReasons("Company Code");
  const nameReasons = fieldReasons("Company Name");
  const codeInlineReasons = inlineCompanyFieldIssues(
    "Company Code",
    codeReasons
  );
  const nameInlineReasons = inlineCompanyFieldIssues(
    "Company Name",
    nameReasons
  );
  const codeReasonsId =
    codeInlineReasons.length > 0
      ? `${index}-company-code-issues`
      : undefined;
  const nameReasonsId =
    nameInlineReasons.length > 0
      ? `${index}-company-name-issues`
      : undefined;

  return (
    <section ref={shellRef} className={s.shell} aria-label={ariaLabel}>
      <div ref={toolbarRef} className={s.toolbarDock}>
        <div className={s.toolbar}>
          <div className={s.jump}>
            <div className={s.searchControl}>
              <FiSearch className={s.searchIcon} aria-hidden="true" />
              <input
                className={s.find}
                role="combobox"
                type="search"
                aria-label="Find a company"
                aria-expanded={suggestionsOpen}
                aria-autocomplete="list"
                aria-controls="company-suggestions"
                aria-activedescendant={
                  suggestionsOpen && activeSuggestion
                    ? `company-suggestion-${activeSuggestion.index}`
                    : undefined
                }
                placeholder="Search by name, code, row or issue"
                value={query}
                disabled={editor !== null}
                onFocus={() => {
                  if (editor) return;
                  setSuggestionsOpen(true);
                  setActiveSuggestionIndex(0);
                }}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setSuggestionsOpen(true);
                  setActiveSuggestionIndex(0);
                }}
                onBlur={() => {
                  setSuggestionsOpen(false);
                  setQuery("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    setSuggestionsOpen(false);
                    setQuery("");
                  } else if (event.key === "ArrowDown") {
                    event.preventDefault();
                    setSuggestionsOpen(true);
                    setActiveSuggestionIndex((current) =>
                      Math.min(current + 1, listedCompanies.length - 1)
                    );
                  } else if (event.key === "ArrowUp") {
                    event.preventDefault();
                    setSuggestionsOpen(true);
                    setActiveSuggestionIndex((current) =>
                      Math.max(current - 1, 0)
                    );
                  }
                  if (event.key === "Enter" && activeSuggestion) {
                    event.preventDefault();
                    chooseCompany(activeSuggestion.index);
                  }
                }}
              />
            </div>
            {suggestionsOpen && !editor && (
              <ul id="company-suggestions" className={s.suggestions} role="listbox">
                {listedCompanies.map((company) => (
                  <li key={company.index} role="none">
                    <button
                      id={`company-suggestion-${company.index}`}
                      type="button"
                      role="option"
                      className={s.suggestion}
                      aria-selected={
                        company.index === activeSuggestion?.index
                      }
                      onMouseEnter={() =>
                        setActiveSuggestionIndex(
                          listedCompanies.findIndex(
                            (candidate) => candidate.index === company.index
                          )
                        )
                      }
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => chooseCompany(company.index)}
                    >
                      <span className={s.companyIndex}>
                        Row {company.sourceRow}
                      </span>
                      <span className={s.companyCode}>{company.code}</span>
                      <span className={s.companyName}>{company.name}</span>
                      {company.issueCount > 0 && (
                        <span className={s.companyIssueCount}>
                          {company.issueCount} issue
                          {company.issueCount === 1 ? "" : "s"}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
                {listedCompanies.length === 0 && (
                  <li className={s.listEmpty}>No companies match.</li>
                )}
              </ul>
            )}
          </div>
          <div className={s.pager}>
            <Tooltip
              text="Previous company"
              placement="top"
              className={s.pagerTooltip}
            >
              <IconButton
                className={s.pagerButton}
                variant="ghost"
                size="small"
                aria-label="Previous company"
                isDisabled={index === 0 || editor !== null}
                onPress={() => chooseCompany(index - 1)}
              >
                <FiChevronLeft size={18} />
              </IconButton>
            </Tooltip>
            <span
              className={s.pagerStatus}
              aria-label={`Company ${index + 1} of ${rows.length}`}
            >
              {index + 1} of {rows.length}
            </span>
            <Tooltip
              text="Next company"
              placement="top"
              className={s.pagerTooltip}
            >
              <IconButton
                className={s.pagerButton}
                variant="ghost"
                size="small"
                aria-label="Next company"
                isDisabled={index === rows.length - 1 || editor !== null}
                onPress={() => chooseCompany(index + 1)}
              >
                <FiChevronRight size={18} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <div className={s.companySummaryRow}>
          <div className={s.companySummary} aria-live="polite">
            <p className={s.selectedCompanyName}>{selectedCompany.name}</p>
            <div className={s.companyMetadata}>
              <span className={s.selectedCompanyCode}>
                {selectedCompany.code}
              </span>
              <span>CSV row {selectedCompany.sourceRow}</span>
              {selectedCompany.issueCount > 0 && (
                <span className={s.companyIssueCount}>
                  {selectedCompany.issueCount} issue
                  {selectedCompany.issueCount === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
          {editable && (
            <button
              ref={editButtonRef}
              type="button"
              className={s.editCompanyButton}
              disabled={isDisabled || editor !== null}
              onClick={openEditor}
            >
              Edit company
            </button>
          )}
        </div>
        <nav className={s.sectionNav} aria-label="Company sections">
          <button
            type="button"
            className={`${s.sectionLink} ${
              activeSection === "identity" ? s.sectionLinkActive : ""
            }`}
            aria-pressed={activeSection === "identity"}
            aria-label={`Identity${
              codeReasons.length + nameReasons.length > 0
                ? `, ${codeReasons.length + nameReasons.length} ${
                    codeReasons.length + nameReasons.length === 1
                      ? "issue"
                      : "issues"
                  }`
                : ""
            }`}
            onClick={() => scrollToGroup("identity")}
          >
            Identity
            {codeReasons.length + nameReasons.length > 0 && (
              <span className={s.sectionIssueCount}>
                {codeReasons.length + nameReasons.length}
              </span>
            )}
          </button>
          {visibleGroups.map((group) => {
            const issueCount = group.headers.reduce(
              (count, header) => count + fieldReasons(header).length,
              0
            );
            const sectionTitle =
              group.id === "company" ? "Company details" : group.title;
            return (
              <button
                type="button"
                key={group.id}
                className={`${s.sectionLink} ${
                  activeSection === group.id ? s.sectionLinkActive : ""
                }`}
                aria-pressed={activeSection === group.id}
                aria-label={`${sectionTitle}${
                  issueCount > 0
                    ? `, ${issueCount} ${
                        issueCount === 1 ? "issue" : "issues"
                      }`
                    : ""
                }`}
                onClick={() => scrollToGroup(group.id)}
              >
                {sectionTitle}
                {issueCount > 0 && (
                  <span className={s.sectionIssueCount}>{issueCount}</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {saveAnnouncement && (
        <p className={s.visuallyHidden} role="status">
          {saveAnnouncement}
        </p>
      )}

      <div className={`${s.form} ${s.previewForm}`}>
        {allReasons.length > 0 && (
          <section
            className={s.issueSummary}
            aria-labelledby={`company-${index}-issues-heading`}
          >
            <h3
              id={`company-${index}-issues-heading`}
              className={s.issueSummaryTitle}
            >
              Issues to review
            </h3>
            <ul className={s.rowIssues}>
              {allReasons.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </section>
        )}

        <header
          id={`company-${index}-identity`}
          className={s.identity}
          tabIndex={-1}
        >
          <h3 className={s.name} aria-describedby={nameReasonsId}>
            {name || "Missing company name"}
          </h3>
          {isEdited("Company Name") && (
            <span className={s.editedStatus}>Edited</span>
          )}
          {nameInlineReasons.length > 0 && (
            <ul id={nameReasonsId} className={s.fieldIssues}>
              {nameInlineReasons.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
          <p className={s.code} aria-describedby={codeReasonsId}>
            <span>Company code</span>
            <strong>{code || "Missing company code"}</strong>
            {isEdited("Company Code") && (
              <span className={s.editedStatus}>Edited</span>
            )}
          </p>
          {codeInlineReasons.length > 0 && (
            <ul id={codeReasonsId} className={s.fieldIssues}>
              {codeInlineReasons.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
        </header>

        {visibleGroups.map((group) => (
          <section
            key={group.id}
            id={`company-${index}-${group.id}`}
            className={s.group}
            aria-labelledby={`company-${index}-${group.id}-heading`}
            tabIndex={-1}
          >
            <h4
              id={`company-${index}-${group.id}-heading`}
              className={s.groupTitle}
            >
              {group.title}
            </h4>
            <div className={s.fields}>
              {group.headers.map((header) => {
                const column = columnsByHeader.get(header);
                if (!column) return null;
                const value = valueFor(header);
                const reasons = fieldReasons(header);
                const inlineReasons = inlineCompanyFieldIssues(
                  header,
                  reasons
                );
                const reasonsId =
                  inlineReasons.length > 0
                    ? `${index}-${headerToIdFragment(header)}-issues`
                    : undefined;
                const fieldPath = Object.prototype.hasOwnProperty.call(
                  CSV_HEADER_TO_FIELD_PATH,
                  header
                )
                  ? CSV_HEADER_TO_FIELD_PATH[header]
                  : undefined;
                const normalised = fieldPath
                  ? normalisationsByCell.get(cellErrorKey(index, fieldPath))
                  : undefined;
                const showLegend =
                  isBlank(display[header] ?? "") && column.legend;
                const legendText =
                  column.requiredness === "required"
                    ? column.legend
                    : `(${column.legend.toLowerCase()})`;
                const legendClass =
                  column.requiredness === "required"
                    ? s.legendRequired
                    : s.legendOptional;

                return (
                  <div key={header} className={s.field}>
                    <div className={s.fieldHead}>
                      <span className={s.fieldLabel}>{header}</span>
                      {showLegend && (
                        <span className={legendClass}>{legendText}</span>
                      )}
                    </div>
                    <span
                      className={`${s.readValue} ${
                        isBlank(value) ? s.emptyValue : ""
                      }`}
                      aria-describedby={reasonsId}
                    >
                      {isBlank(value) ? "—" : value}
                    </span>
                    {isEdited(header) && (
                      <span className={s.editedStatus}>Edited</span>
                    )}
                    {normalised !== undefined && (
                      <span className={s.normalised}>
                        Normalised: {normalised}
                      </span>
                    )}
                    {inlineReasons.length > 0 && (
                      <ul id={reasonsId} className={s.fieldIssues}>
                        {inlineReasons.map((message) => (
                          <li key={message}>{message}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      {editor && (
        <CompanyShellEditModal
          isOpen={
            !discardConfirmOpen && !suppressDiscardConfirmation
          }
          rowIndex={editor.rowIndex}
          sourceRow={
            rowCsvLineNumbers?.[editor.rowIndex] ?? editor.rowIndex + 1
          }
          columns={columns}
          groups={visibleGroups}
          draft={editor.draft}
          rowIssues={allReasons}
          cellErrors={cellErrors}
          normalisations={normalisations}
          isDirty={editorIsDirty}
          isProcessing={isDisabled}
          onDraftChange={(draft) =>
            setEditor((current) =>
              current ? { ...current, draft } : current
            )
          }
          onCancel={requestCancelEditor}
          onSave={saveEditor}
        />
      )}
      <DiscardCompanyChangesConfirmModal
        isOpen={discardConfirmOpen && !suppressDiscardConfirmation}
        onStay={() => setDiscardConfirmOpen(false)}
        onDiscard={closeEditor}
      />
    </section>
  );
}
